import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, firstValueFrom, timeout } from 'rxjs';

import { AddTaskComponent } from '../add-task/add-task';
import { ProjectService } from '../../../shared/services/project.service';
import { TaskService } from '../../../shared/services/task.service';
import { UserService } from '../../../shared/services/user.service';
import { Project, ProjectStage } from '../../../shared/models/project.model';
import { Task } from '../../../shared/models/task.model';
import { User as UserModel } from '../../../shared/models/user.model';

/** Give the shared stores a bounded chance to fill before reporting "not found",
 *  so a store that never emits shows an error instead of a skeleton forever. */
const STORE_WAIT_MS = 8000;

/**
 * Full-page host for the task details view, replacing the modal that used to wrap
 * AddTaskComponent. Like TaskCreatePageComponent it owns no form logic: it resolves
 * the task and its project context from the route and hands them over.
 */
@Component({
  selector: 'app-task-detail-page',
  imports: [CommonModule, AddTaskComponent],
  templateUrl: './task-detail-page.html',
})
export class TaskDetailPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectService = inject(ProjectService);
  private taskService = inject(TaskService);
  private userService = inject(UserService);

  projectId!: string;
  taskId!: string;

  project?: Project;
  stage?: ProjectStage;
  task: Task | null = null;
  employees: UserModel[] = [];

  loading = true;
  /** Set when the route points at a task that does not exist (or was deleted). */
  notFound = false;

  async ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('id')!;
    this.taskId = this.route.snapshot.paramMap.get('taskId')!;

    // The stores are initialised once in app.ts; here we only listen.
    await this.resolveContext();
  }

  private async resolveContext() {
    try {
      const projects = await firstValueFrom(
        this.projectService.projects$.pipe(filter(p => p.length > 0), timeout(STORE_WAIT_MS))
      );
      this.project = projects.find(p => p.projectId === this.projectId);

      const task = await this.resolveTask();
      if (!this.project || !task) {
        this.notFound = true;
        return;
      }
      this.task = task;

      const stages = await firstValueFrom(
        this.projectService.projectsStage$.pipe(filter(s => s.length > 0), timeout(STORE_WAIT_MS))
      );
      this.stage = stages.find(s => s.projectStageId === task.projectStageId);

      if (this.project.tenantId) {
        // Archived users are kept: the pickers filter them out themselves while
        // still showing anyone already assigned.
        this.employees = await this.userService.getUsersByTenantId(this.project.tenantId);
      }
    } catch (e) {
      console.error('[TaskDetailPage] Failed to resolve task context', e);
      this.notFound = true;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Reads the task from the shared store, waiting for the first non-empty
   * emission so a still-loading store is not mistaken for a missing task.
   */
  private async resolveTask(): Promise<Task | null> {
    const snapshot = this.taskService.getTasksSnapshot();
    const found = snapshot.find(t => t.id === this.taskId);
    if (found) return found;

    if (snapshot.length === 0) {
      const tasks = await firstValueFrom(
        this.taskService.tasks$.pipe(filter(t => t.length > 0), timeout(STORE_WAIT_MS))
      );
      return tasks.find(t => t.id === this.taskId) ?? null;
    }
    return null;
  }

  /** Same shape as DetailProjectComponent's getter — drives the edit permissions. */
  get projectManagerUserIds(): string[] {
    return this.project?.managers?.map(m => m.userId).filter(Boolean) || [];
  }

  /** Both closing and deleting land back on the project. */
  goToProject() {
    this.router.navigate(['/projects', this.projectId]);
  }
}
